import { supabase } from "@/integrations/supabase/client";
import { MOCK_PROPERTIES, type Purpose } from "@/lib/mock-data";

/* ------------------------------------------------------------------ *
 * Unified read model for the Property Details page.
 * Reads the independent listing tables (sale / rent / lease / parking),
 * falls back to the legacy `properties` table and finally to mock data
 * so the page never breaks in demo mode.
 * ------------------------------------------------------------------ */

export interface PropertyDetail {
  id: string;
  kind: Purpose;
  title: string;
  priceLabel: string;
  propertyType: string;
  areaSqft: number | null;
  description: string;
  amenities: string[];
  images: string[];
  address: string;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  postedAt: string | null;
  ownerId: string | null;
  partnerName: string | null;
  verified: boolean;
  enquiryEnabled: boolean;
  contact: {
    showNumber: boolean;
    showWhatsapp: boolean;
    allowUnlock: boolean;
    phone: string | null;
    whatsapp: string | null;
  };
  demo: boolean;
}

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=70";

function inr(n: number) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

function imageList(image_url: unknown, images: unknown): string[] {
  const list = Array.isArray(images)
    ? (images as unknown[]).filter((i): i is string => typeof i === "string")
    : [];
  const first = typeof image_url === "string" && image_url ? [image_url] : [];
  const all = [...first, ...list.filter((i) => i !== image_url)];
  return all.length ? all : [FALLBACK_IMAGE];
}

type Row = Record<string, unknown>;

const str = (r: Row, k: string) => (typeof r[k] === "string" ? (r[k] as string) : null);
const num = (r: Row, k: string) => (r[k] == null ? null : Number(r[k]));
const bool = (r: Row, k: string, dflt = false) =>
  typeof r[k] === "boolean" ? (r[k] as boolean) : dflt;

function commonFields(
  r: Row,
  kind: Purpose,
): Omit<PropertyDetail, "priceLabel" | "propertyType" | "amenities"> {
  return {
    id: String(r.id),
    kind,
    title: str(r, "title") ?? "Listing",
    areaSqft: num(r, "area_sqft"),
    description: str(r, "description") ?? "No description provided by the owner.",
    images: imageList(r.image_url, r.images),
    address: str(r, "address") ?? "—",
    city: str(r, "city"),
    latitude: num(r, "latitude"),
    longitude: num(r, "longitude"),
    postedAt: str(r, "created_at"),
    ownerId: str(r, "owner_id"),
    partnerName: null,
    verified: bool(r, "verified"),
    enquiryEnabled: bool(r, "enquiry_enabled", true),
    contact: {
      showNumber: bool(r, "show_number"),
      showWhatsapp: bool(r, "show_whatsapp"),
      allowUnlock: bool(r, "allow_contact_unlock", true),
      phone: null,
      whatsapp: null,
    },
    demo: false,
  };
}

function highlightList(r: Row): string[] {
  return Array.isArray(r.highlights)
    ? (r.highlights as unknown[]).filter((h): h is string => typeof h === "string")
    : [];
}

function parkingAmenities(r: Row): string[] {
  const list: string[] = [];
  if (bool(r, "cctv")) list.push("CCTV");
  if (bool(r, "security")) list.push("Security");
  if (bool(r, "lighting")) list.push("Lighting");
  if (bool(r, "ev_charging")) list.push("EV charging");
  if (bool(r, "open_24h")) list.push("Open 24×7");
  const vehicles = Array.isArray(r.vehicle_types)
    ? (r.vehicle_types as unknown[]).filter((v): v is string => typeof v === "string")
    : [];
  return [...list, ...vehicles.map((v) => `${v.toUpperCase()} allowed`)];
}

function parkingPrice(r: Row): string {
  const hourly = num(r, "price_hourly");
  const daily = num(r, "price_daily");
  const monthly = num(r, "price_monthly");
  if (hourly) return `${inr(hourly)} / hr`;
  if (daily) return `${inr(daily)} / day`;
  if (monthly) return `${inr(monthly)} / mo`;
  return "Price on request";
}

async function partnerNameFor(ownerId: string | null): Promise<string | null> {
  if (!ownerId) return null;
  const { data } = await supabase
    .from("partner_profiles")
    .select("business_name")
    .eq("user_id", ownerId)
    .eq("status", "approved")
    .maybeSingle();
  return (data?.business_name as string | undefined) ?? null;
}

async function readTable(
  table: "sale_listings" | "rent_listings" | "lease_listings" | "parking_listings",
  id: string,
) {
  const { data } = await supabase.from(table).select("*").eq("id", id).maybeSingle();
  return (data as Row | null) ?? null;
}

function fromMock(id: string): PropertyDetail | null {
  const p = MOCK_PROPERTIES.find((m) => m.id === id);
  if (!p) return null;
  return {
    id: p.id,
    kind: p.purpose,
    title: p.title,
    priceLabel: p.priceLabel,
    propertyType: p.type,
    areaSqft: p.areaSqft,
    description: p.description,
    amenities: p.highlights,
    images: [p.image],
    address: p.location,
    city: p.location.split(",").pop()?.trim() ?? null,
    latitude: null,
    longitude: null,
    postedAt: null,
    ownerId: null,
    partnerName: "Landoo Verified Partner",
    verified: true,
    enquiryEnabled: true,
    contact: {
      showNumber: false,
      showWhatsapp: false,
      allowUnlock: true,
      phone: null,
      whatsapp: null,
    },
    demo: true,
  };
}

export async function fetchPropertyDetail(id: string): Promise<PropertyDetail | null> {
  const isUuid = /^[0-9a-f-]{36}$/i.test(id);
  if (!isUuid) return fromMock(id);

  const [sale, rent, lease, parking] = await Promise.all([
    readTable("sale_listings", id).catch(() => null),
    readTable("rent_listings", id).catch(() => null),
    readTable("lease_listings", id).catch(() => null),
    readTable("parking_listings", id).catch(() => null),
  ]);

  let detail: PropertyDetail | null = null;

  if (sale) {
    detail = {
      ...commonFields(sale, "sale"),
      priceLabel: str(sale, "price_label") ?? inr(num(sale, "price") ?? 0),
      propertyType: str(sale, "listing_type") ?? "Property",
      amenities: highlightList(sale),
    };
  } else if (rent) {
    detail = {
      ...commonFields(rent, "rent"),
      priceLabel: `${inr(num(rent, "rent_monthly") ?? 0)} / mo`,
      propertyType: str(rent, "listing_type") ?? "Property",
      amenities: highlightList(rent),
    };
  } else if (lease) {
    detail = {
      ...commonFields(lease, "lease"),
      priceLabel: `${inr(num(lease, "lease_amount") ?? 0)} lease`,
      propertyType: str(lease, "listing_type") ?? "Property",
      amenities: highlightList(lease),
    };
  } else if (parking) {
    detail = {
      ...commonFields(parking, "parking"),
      priceLabel: parkingPrice(parking),
      propertyType: str(parking, "parking_type") ?? "Parking place",
      amenities: parkingAmenities(parking),
    };
    detail.partnerName = str(parking, "provider_name");
  } else {
    const { data } = await supabase
      .from("properties")
      .select("*, property_types(label)")
      .eq("id", id)
      .maybeSingle();
    const row = data as Row | null;
    if (row) {
      const types = row.property_types as { label?: string } | null;
      detail = {
        ...commonFields(row, (str(row, "purpose") as Purpose) ?? "sale"),
        priceLabel: str(row, "price_label") ?? inr(num(row, "price") ?? 0),
        propertyType: types?.label ?? str(row, "listing_type") ?? "Property",
        amenities: highlightList(row),
      };
    }
  }

  if (!detail) return null;
  if (!detail.partnerName)
    detail.partnerName = await partnerNameFor(detail.ownerId).catch(() => null);
  return detail;
}
